import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import heic2any from 'heic2any';
import { Jimp } from 'jimp';
import { NovoPedidoAnexoRequest } from '../models/request/novo-pedido.request.model';
import {
  ANEXO_EXTENSOES_PERMITIDAS,
  ANEXO_QUANTIDADE_MAXIMA,
  ANEXO_TAMANHO_MAXIMO_BYTES,
  formatarTamanhoArquivo,
  validarExtensaoAnexo,
  validarTamanhoAnexo
} from '../utils/anexo-validacao';

/**
 * Regras de anexos (formato, tamanho e quantidade) compartilhadas entre as
 * telas de Ordem de Serviço e Venda (Pedido sem receita).
 */
@Injectable({ providedIn: 'root' })
export class AnexoUploadService {

  constructor(private toastr: ToastrService) { }

  async processarSelecao(files: File[], quantidadeJaAnexada: number): Promise<NovoPedidoAnexoRequest[]> {
    const novosAnexos: NovoPedidoAnexoRequest[] = [];
    let totalAtual = quantidadeJaAnexada;

    for (const file of files) {
      if (!(file instanceof File)) {
        continue;
      }

      if (totalAtual >= ANEXO_QUANTIDADE_MAXIMA) {
        this.toastr.error(`É permitido anexar no máximo ${ANEXO_QUANTIDADE_MAXIMA} arquivos por pedido.`, 'Limite de arquivos excedido');
        break;
      }

      const filename = file.name.replace(/\..+$/, "");
      let value: File = file;

      try {
        if (/\.heic$/i.test(value.name)) {
          const result: any = await heic2any({
            blob: value,
            toType: "jpg"
          });

          value = new File([result], `${filename}.jpg`);
        }
        else if (/\.(webp)$/i.test(value.name)) {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const img = new Image();
            const promise = new Promise<any>((resolve) => img.onload = resolve);
            img.src = URL.createObjectURL(value);
            await promise;

            canvas.width = img.width;
            canvas.height = img.height;

            ctx.drawImage(img, 0, 0);
            const pngDataUrl = await new Promise<any>((resolve) => canvas.toBlob(resolve));
            value = new File([pngDataUrl], `${filename}.png`);
          }
        }

        if (/\.(png|bmp|gif|tiff)$/i.test(value.name)) {
          const image = await Jimp.fromBuffer(await value.arrayBuffer());
          const image_jpg: any = await image.getBuffer("image/jpeg", {
            quality: 60
          });

          value = new File([image_jpg], `${filename}.jpg`);
        }
      }
      catch (e) {
        console.error(e);
        this.toastr.error(`Não foi possível processar o arquivo "${file.name}".`, 'Erro ao processar anexo');
        continue;
      }

      if (!validarExtensaoAnexo(value.name)) {
        this.toastr.error(
          `O arquivo "${file.name}" possui um formato não permitido. Formatos aceitos: ${ANEXO_EXTENSOES_PERMITIDAS.join(', ')}.`,
          'Formato inválido'
        );
        continue;
      }

      if (!validarTamanhoAnexo(value.size)) {
        this.toastr.error(
          `O arquivo "${file.name}" tem ${formatarTamanhoArquivo(value.size)}, acima do limite permitido de ${formatarTamanhoArquivo(ANEXO_TAMANHO_MAXIMO_BYTES)}.`,
          'Arquivo muito grande'
        );
        continue;
      }

      novosAnexos.push({
        nome_arquivo: value.name,
        tipo_conteudo: value.type,
        descricao: "Receita",
        arquivo: value,
        ref: file
      } as any);

      totalAtual++;
    }

    return novosAnexos;
  }
}
